import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';

/**
 * How long a map took, live → finish (GET /api/matches/:id/map-times), shown under the status
 * pill of the match page. A running map ticks every second; "All maps" shows the total.
 * `anonymous` = public page: no login header, so a stream-delayed match gets the delayed times.
 */
const publicApi = axios.create();
delete publicApi.defaults.headers.common['Authorization'];

export function formatDuration(totalSeconds) {
  const s = Math.max(0, Math.floor(totalSeconds));
  const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60), sec = s % 60;
  const pad = (n) => String(n).padStart(2, '0');
  return h ? `${h}:${pad(m)}:${pad(sec)}` : `${m}:${pad(sec)}`;
}

// Per-map live start / end of a match (load on open / match or status change; while live, again
// every 30 s for a map end or a new map). Shared by the duration and the match page's date line.
export function useMapTimes(matchId, status, anonymous = false) {
  const [data, setData] = useState(null);
  useEffect(() => {
    if (!matchId) return undefined;
    let alive = true;
    const load = () => (anonymous ? publicApi : axios).get(`/api/matches/${matchId}/map-times`)
      .then(r => { if (alive) setData({ ...r.data, skew: Date.now() - new Date(r.data.now).getTime() }); })
      .catch(() => { });
    load();
    const iv = status === 'live' ? setInterval(() => { if (!document.hidden) load(); }, 30000) : null;
    return () => { alive = false; if (iv) clearInterval(iv); };
  }, [matchId, status, anonymous]);
  return data;
}

// `times` = useMapTimes() result when the page already loads it (else the component loads it itself)
export default function MapDuration({ matchId, mapIndex, status, anonymous = false, style, times }) {
  const { t } = useTranslation();
  const own = useMapTimes(times === undefined ? matchId : null, status, anonymous);
  const data = times === undefined ? own : times;
  const [now, setNow] = useState(Date.now());

  const maps = (data && data.maps) || [];
  const running = maps.some(m => !m.ended_at);
  useEffect(() => {
    if (!running) return undefined;
    const iv = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(iv);
  }, [running]);
  if (!data || !maps.length) return null;

  // server clock + the public stream delay: a running map's time never runs ahead of the page
  const serverNow = now - data.skew - (data.delay_seconds || 0) * 1000;
  const secsOf = (m) => ((m.ended_at ? new Date(m.ended_at).getTime() : serverNow) - new Date(m.started_at).getTime()) / 1000;

  let secs; let live = false; let label;
  if (mapIndex === 'all') {
    secs = maps.reduce((sum, m) => sum + secsOf(m), 0);
    live = running;
    label = t('matchDuration.total', 'Total time of all maps (live → finish)');
  } else {
    const m = maps.find(x => x.map_index === Number(mapIndex));
    if (!m) return null;
    secs = secsOf(m);
    live = !m.ended_at;
    label = live
      ? t('matchDuration.running', 'Map {{n}} running since it went live', { n: Number(mapIndex) + 1 })
      : t('matchDuration.mapTime', 'Map {{n}}: time from going live to the end', { n: Number(mapIndex) + 1 });
  }

  return (
    <span title={label} style={{
      display: 'inline-flex', alignItems: 'center', gap: 5, fontFamily: 'var(--font-mono)', fontSize: '0.72rem', fontWeight: 600,
      color: live ? 'var(--accent-green, #22c55e)' : 'var(--text-secondary, #8b8b9e)', letterSpacing: '0.02em', ...style,
    }}>
      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" style={{ opacity: 0.8 }}>
        <circle cx="12" cy="13" r="8" /><path d="M12 9v4l2.5 2.5" /><path d="M9 2h6" />
      </svg>
      {mapIndex === 'all' && <span style={{ opacity: 0.75 }}>{t('matchDuration.totalShort', 'Total')}</span>}
      {formatDuration(secs)}
    </span>
  );
}
